import React, { useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchOnboardingStage } from "../../../redux/attorneyDataSlice";
import "./onboarding.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "../assets/settingsIcon.png";
import IntakeIcon from "../assets/intakeIcon.png";
import PersonIcon from "../assets/personIcon.png";
import ClientIcon from "../assets/clientIcon.png";
import RecIcon from "../assets/recIcon.png";

const Onboarding = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdmin = useSelector((s) => s.attorneyData.attorneyData?.admin);
  const userData = useSelector((s) => s.attorneyData.attorneyData);

  // onboardingStep is a 1-based integer.
  const onboardingStep = useSelector(
    (state) => state.attorneyData.attorneyData?.onboarding_stage
  );

  useEffect(() => {
    // Fetch fresh onboarding stage when component mounts
    if (userData?.user_id) {
      dispatch(fetchOnboardingStage(userData.user_id))
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, userData?.user_id]);

  // Don't render if userData is not loaded yet
  if (!userData) {
    return null;
  }

  // Example checklist data — replace with your actual API response or logic
  const adminChecklistData = [
    {
      title: "Create your profile",
      status: "Not started",
      icon: PersonIcon,
      steps: [
        {
          title: "Onboard yourself",
          description: "Finish updating your details",
          url: "/attorney/setup/add",
        },
        {
          title: "Set up your payment method",
          description:
            "Enable payments to start accepting client bookings right away.",
          url: "/attorney/setup/payment",
        },
        {
          title: "Set up your availability",
          description: "Add your hours to accept client bookings.",
          url: "/attorney/setup/availability",
        },
        // {
        //   title: "Verify your identity",
        //   description: "Complete ID verification to ensure compliance.",
        //   url: "/attorney/setup/verify",
        // },
      ],
    },
    {
      title: "Customize your settings",
      status: "Not started",
      icon: SettingsIcon,
      steps: [
        {
          title: "Select video visit location(s)",
          description:
            "Specify the office location(s) you’ll use for video calls.",
          url: "/attorney/setup/location",
        },
        {
          title: "Review your visit reasons",
          description: "Confirm the legal consultation types you offer.",
          url: "/attorney/setup/visit-reasons",
        },
        {
          title: "Review our pricing policies",
          description: "Acknowledge platform fees and add a payment method.",
          url: "/attorney/settings?tab=pricing",
        },
      ],
    },
    {
      title: "Set up your client intake experience",
      status: "Not started",
      icon: IntakeIcon,
      steps: [
        {
          title: "Upload your practice forms",
          description:
            "Upload necessary documents (e.g., retainer agreements).",
          url: "/attorney/intake/forms",
        },
      ],
    },
    {
      title: "Start seeing clients",
      status: "Not started",
      icon: ClientIcon,
      steps: [
        {
          title: "Activate your practice on Quidpro Marketplace",
          description: "Enable your listing so new clients can find you.",
          url: "/attorney/setup/activate-firm",
        },
        // {
        //   title: "Add the Book Online button",
        //   description: "Install the booking widget on your own website.",
        //   url: "/attorney/clients/book-online",
        // },
        // {
        //   title: "Display availability on Google",
        //   description:
        //     "Sync your schedule to show up in Google search results.",
        //   url: "/attorney/clients/google-sync",
        // },
      ],
    },
  ];

  const attorneyChecklistData = [
    {
      title: "Create your profile",
      status: "Not started",
      icon: PersonIcon,
      steps: [
        {
          title: "Onboard yourself",
          description: "Finish updating your details",
          url: "/attorney/setup/add",
        },
        {
          title: "Set up your availability",
          description: "Add your hours to accept client bookings.",
          url: "/attorney/setup/availability",
        },
        // {
        //   title: "Verify your identity",
        //   description: "Complete ID verification to ensure compliance.",
        //   url: "/attorney/setup/verify",
        // },
      ],
    },
    {
      title: "Customize your settings",
      status: "Not started",
      icon: SettingsIcon,
      steps: [
        {
          title: "Select video visit location(s)",
          description:
            "Specify the office location(s) you’ll use for video calls.",
          url: "/attorney/setup/location",
        },
        {
          title: "Review your visit reasons",
          description: "Confirm the legal consultation types you offer.",
          url: "/attorney/setup/visit-reasons",
        },
      ],
    },
    {
      title: "Set up your client intake experience",
      status: "Not started",
      icon: IntakeIcon,
      steps: [
        {
          title: "Upload your practice forms",
          description:
            "Upload necessary documents (e.g., retainer agreements).",
          url: "/attorney/intake/forms",
        },
      ],
    },
    {
      title: "Start seeing clients",
      status: "Not started",
      icon: ClientIcon,
      steps: [
        // {
        //   title: "Display availability on Google",
        //   description:
        //     "Sync your schedule to show up in Google search results.",
        //   url: "/attorney/clients/google-sync",
        // },
        {
          title: "Activate your profile on Quidpro Marketplace",
          description: "Enable your listing so new clients can find you.",
          url: "/attorney/setup/activate-profile",
        },
      ],
    },
  ];

  const onboardingData = isAdmin ? adminChecklistData : attorneyChecklistData;

  // We'll use this counter to assign a global order to each step.
  let stepCounter = 0;

  return (
    <div className="attorney-home-container">
      <main className="attorney-home-main">
        <div className="attorney-home-main-left">
          {/* Onboarding Checklist */}
          <section className="attorney-home-checklist-section">
            <h2 className="attorney-home-checklist-title">
              Your setup checklist
            </h2>

            {onboardingData.map((item, sectionIndex) => (
              <Accordion
                key={sectionIndex}
                className="attorney-home-accordion"
                square={true}
                sx={{
                  border: "1px solid #E8E9EB",
                  backgroundColor: "#F9F8F7",
                  borderRadius: "8px",
                  boxShadow: "none",
                  overflow: "hidden",
                  "&.Mui-expanded": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${sectionIndex}-content`}
                  id={`panel${sectionIndex}-header`}
                  className="attorney-home-accordion-summary"
                  sx={{
                    height: "70px",
                    "&.Mui-expanded": {
                      backgroundColor: "#F9F8F7 !important",
                    },
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {item.icon && (
                      <img
                        src={item.icon}
                        alt="Section Icon"
                        style={{
                          width: "16px",
                          height: "16px",
                          marginRight: "8px",
                        }}
                      />
                    )}
                    <div className="attorney-home-accordion-title">
                      {item.title}
                    </div>
                  </div>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    marginTop: 5,
                  }}
                  className="attorney-home-accordion-details"
                >
                  {item.steps.map((step, stepIndex) => {
                    // Increase the global step counter.
                    stepCounter++;
                    // Determine the status of the step.
                    const isDone = stepCounter < onboardingStep;
                    const isCurrent = stepCounter === onboardingStep;
                    const isLocked = stepCounter > onboardingStep;

                    return (
                      <div className="outer-row" key={stepIndex}>
                        <div className="attorney-home-step-row">
                          <div className="attorney-home-step-text">
                            <div className="step-title">{step.title}</div>
                            <div className="step-description">
                              {step.description}
                            </div>
                          </div>
                          {isDone ? (
                            // Display a green "Done" box.
                            <button className="attorney-home-step-done">
                              Done
                            </button>
                          ) : (
                            // For current or locked steps, display the "Get started" button.
                            <button
                              className={`attorney-home-step-action ${
                                isLocked ? "locked" : ""
                              }`}
                              onClick={() => {
                                // Only allow navigation if it's the current step and a URL is provided.
                                if (isCurrent && step.url) {
                                  navigate(step.url);
                                }
                              }}
                              disabled={!isCurrent || !step.url}
                            >
                              Get started
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </section>

          {/* Recommendations Section */}
          <section className="attorney-home-recommendations">
            <h3 className="attorney-home-recommendations-title">
              Recommendations for your account
            </h3>
            <div className="attorney-home-recommendations-box">
              <div style={{ display: "flex", marginBottom: "20px" }}>
                <img
                  src={RecIcon}
                  style={{
                    width: "75px",
                    height: "40px",
                    marginRight: "15px",
                  }}
                  alt="Recommendation Icon"
                />
                <p className="recommendations-subtitle">
                  Get dedicated 1:1 support from quidpro
                </p>
              </div>
              <p className="recommendations-content">
                Our helpful team will walk you through selecting the right
                products and solutions for your practice, setting up your
                account, and answer any questions you may have.
              </p>
              <button className="attorney-home-upgrade-button">
                Schedule a call
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
